import React, {
 useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import { qlog, qapi } from '../v1_adapter';
import OutletLogo from './OutletLogo';

/**
 * Custom option renderer for dropdown
 * Displays outlet name, domain, and logo
 */
function CustomOption({ data, innerRef, innerProps }) {
  const ariaLabel = `${data.label}${data.domain ? ` - ${data.domain}` : ''}`;

  const handleKeyDown = (e) => {
    // Handle Enter and Space keys for keyboard accessibility
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (innerProps.onClick) {
        innerProps.onClick();
      }
    }
  };

  return (
    <div
      ref={innerRef}
      onClick={innerProps.onClick}
      onKeyDown={handleKeyDown}
      role="option"
      aria-label={ariaLabel}
      aria-selected={false}
      tabIndex={0}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
        cursor: 'pointer',
      }}
    >
      <OutletLogo imageUrl={data.imageUrl} name={data.label} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: '500' }}>{data.label}</div>
        {data.domain && (
          <small
            style={{
              color: '#666',
              fontSize: '0.85rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
            }}
          >
            {data.domain}
          </small>
        )}
      </div>
    </div>
  );
}

/**
 * Custom menu wrapper with progressive disclosure for create button
 * Initially shows a small text link, expands to full button after click
 * This prevents mindless outlet creation by making the button less obvious
 */
function CustomMenu({ children, selectProps }) {
  const { onCreateClick, currentInputValue } = selectProps || {};
  const [showCreateButton, setShowCreateButton] = useState(false);

  const handleShowButton = () => {
    setShowCreateButton(true);
  };

  const handleCreateClick = () => {
    if (onCreateClick) {
      onCreateClick(currentInputValue);
    }
  };

  return (
    <div>
      {children}
      {onCreateClick && (
        <div
          style={{
            borderTop: '1px solid #e0e0e0',
            padding: '8px',
            marginTop: '4px',
          }}
        >
          {!showCreateButton ? (
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                padding: '4px',
                fontSize: '0.85rem',
                textDecoration: 'none',
                textAlign: 'center',
                width: '100%',
              }}
              onClick={handleShowButton}
              aria-label="Can't find what you're looking for? Click to create a new media outlet"
            >
              Can&apos;t find what you&apos;re looking for?
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm w-100"
              onClick={handleCreateClick}
              style={{ fontSize: '0.9rem' }}
              aria-label="Create a new media outlet"
            >
              <i className="fa fa-plus me-1" />
              Create New Outlet
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Enhanced media outlet selector with custom rendering
 * Displays outlet logos and domain information with a create button
 *
 * @param {Object} props
 * @param {number} props.value - Current selected outlet ID
 * @param {function} props.onChange - Callback when selection changes
 * @param {function} props.onCreateClick - Callback when create button is clicked
 * @returns {React.ReactElement} AsyncSelect component with custom renderers
 */
function MediaSelect({
 value = '', hasError, onChange, onCreateClick = null,
}) {
  const [selectedOption, setSelectedOption] = useState(null);
  const [searchOptions, setSearchOptions] = useState([]);
  const [isLoadingSelected, setIsLoadingSelected] = useState(false);
  const [currentInputValue, setCurrentInputValue] = useState('');
  const loadingValueRef = useRef(null);

  const defaultOptions = useMemo(
    () => [...(selectedOption ? [selectedOption] : []), ...searchOptions.filter((opt) => opt.value !== value)],
    [selectedOption, searchOptions, value],
  );

  const promiseOptions = useCallback(async (inputValue) => {
    try {
      const results = await qapi.mediaOutlets.getAll({ search: inputValue });
      setSearchOptions(results);
      return results;
    } catch (error) {
      qlog.error('Failed to load media options:', error);
      return [];
    }
  }, []);

  // Load selected option when value changes
  useEffect(() => {
    const controller = new AbortController();

    if (value && (!selectedOption || selectedOption.value !== value) && loadingValueRef.current !== value) {
      const existingOption = searchOptions.find((opt) => opt.value === value);

      if (existingOption) {
        setSelectedOption(existingOption);
      } else {
        setIsLoadingSelected(true);

        loadingValueRef.current = value;
        qapi.mediaOutlets
          .getAll({ id: value, signal: controller.signal })
          .then((results) => {
            // Only update state if the value hasn't changed since request started
            if (loadingValueRef.current === value && results.length > 0) {
              setSelectedOption(results[0]);
            }
            setIsLoadingSelected(false);
          })
          .catch((error) => {
            // Ignore abort errors (expected when component unmounts or value changes)
            if (error.name !== 'AbortError') {
              qlog.error('Failed to load selected outlet:', error);
            }
            setIsLoadingSelected(false);
          });
      }
    } else if (!value) {
      setSelectedOption(null);
    }

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <AsyncSelect
      loadOptions={promiseOptions}
      defaultOptions={defaultOptions}
      onChange={(option) => onChange(option?.value || '')}
      onInputChange={(inputValue) => setCurrentInputValue(inputValue)}
      value={selectedOption}
      isLoading={isLoadingSelected}
      isClearable
      components={{
        Option: CustomOption,
        MenuList: CustomMenu,
      }}
      onCreateClick={onCreateClick}
      currentInputValue={currentInputValue}
      className={`media-select ${hasError ? 'is-invalid' : ''}`}
      classNamePrefix="media-select"
      placeholder="Search for a media outlet..."
      noOptionsMessage={() => (currentInputValue.length > 0 ? 'No outlets found' : null)}
      styles={{
        menuList: (provided) => ({
          ...provided,
          maxHeight: '300px',
        }),
      }}
    />
  );
}

CustomOption.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    domain: PropTypes.string,
    imageUrl: PropTypes.string,
  }).isRequired,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.node })]),
  innerProps: PropTypes.shape({
    onClick: PropTypes.func,
  }),
};

CustomOption.defaultProps = {
  innerRef: null,
  innerProps: {},
};

CustomMenu.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.shape({
    onCreateClick: PropTypes.func,
    currentInputValue: PropTypes.string,
  }),
};

CustomMenu.defaultProps = {
  children: null,
  selectProps: {},
};

MediaSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasError: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onCreateClick: PropTypes.func,
};

MediaSelect.defaultProps = {
  value: null,
  onCreateClick: null,
};

export default MediaSelect;
